import { useEffect, useId, useMemo, useRef } from 'react';
import { ExternalLink, MessageCircle, X } from 'lucide-react';
import qrcode from 'qrcode-generator';

type Props = { link: string; expiraEm: string; aoFechar: () => void };

/** QR code em SVG (sem imagem externa): o celular lê e abre o bot já com o código. */
function QrCode({ texto }: { texto: string }) {
  const svg = useMemo(() => {
    const qr = qrcode(0, 'M');
    qr.addData(texto);
    qr.make();
    return qr.createSvgTag({ cellSize: 6, margin: 2, scalable: true });
  }, [texto]);
  return <div className="painel-telegram__qr" role="img" aria-label="QR code do link do Telegram" dangerouslySetInnerHTML={{ __html: svg }} />;
}

const hora = (iso: string) => new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

/**
 * Vínculo do Telegram no computador (RQ-MSG-007): o Telegram fica no celular, então o link vira um QR code para ler
 * com a câmera, com a opção de mandar o link para si mesmo no WhatsApp ou abrir aqui (Telegram Desktop).
 */
export function PainelTelegram({ link, expiraEm, aoFechar }: Props) {
  const ref = useRef<HTMLDialogElement>(null);
  const id = useId();
  useEffect(() => {
    const d = ref.current;
    if (d && !d.open) d.showModal();
    return () => d?.close();
  }, []);
  const whatsapp = `https://wa.me/?text=${encodeURIComponent(`Vincular meu Telegram ao Nexital: ${link}`)}`;

  return (
    <dialog
      ref={ref}
      className="dialogo painel-telegram"
      aria-labelledby={`${id}-titulo`}
      onCancel={(e) => {
        e.preventDefault();
        aoFechar();
      }}
    >
      <button type="button" className="painel-telegram__fechar" aria-label="Fechar" onClick={aoFechar}>
        <X size={18} aria-hidden />
      </button>
      <h2 id={`${id}-titulo`}>Vincular o Telegram</h2>
      <p className="painel-telegram__texto">Aponte a câmera do celular para o código e toque em <strong>Iniciar</strong> no Telegram.</p>
      <QrCode texto={link} />
      <p className="painel-telegram__validade">O código vale até {hora(expiraEm)} e serve uma vez só.</p>
      <div className="painel-telegram__acoes">
        <a className="botao-whatsapp" href={whatsapp} target="_blank" rel="noopener noreferrer">
          <MessageCircle size={18} aria-hidden /> Enviar o link pelo WhatsApp
        </a>
        <a className="botao-secundario" href={link} target="_blank" rel="noopener noreferrer">
          <ExternalLink size={16} aria-hidden /> Abrir no Telegram deste computador
        </a>
      </div>
    </dialog>
  );
}
